import { IdcardOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Form, Input, Space, Typography, message, theme } from 'antd';
import { CardHeader } from 'components/headings';
import { actualizarMisDatos } from 'api/me';
import React, { useMemo, useState } from 'react';

const { Text } = Typography;
const { useToken } = theme;

/**
 * Derecho de rectificación, acotado a lo que la persona puede corregir sola.
 *
 * El RUT se muestra pero no se edita: el backend rechaza el campo con 400 y, más
 * importante, identifica los registros de los trabajos realizados. Mostrarlo
 * deshabilitado sin explicar por qué se lee como una falla de la pantalla, así
 * que la razón va escrita al lado, en lenguaje simple.
 */
const CuentaPanel = ({ cuenta, onActualizado, panelStyle }) => {
  const { token } = useToken();
  const [form] = Form.useForm();
  const [guardando, setGuardando] = useState(false);

  // Se re-evalúa en cada tecla vía Form.useWatch para poder apagar el botón
  // cuando no hay nada que guardar.
  const fullname = Form.useWatch('fullname', form);
  const email = Form.useWatch('email', form);

  const sinCambios = useMemo(() => {
    const nombreActual = fullname ?? cuenta.fullname;
    const correoActual = email ?? cuenta.email;
    return nombreActual === cuenta.fullname && correoActual === cuenta.email;
  }, [fullname, email, cuenta.fullname, cuenta.email]);

  const guardar = async (valores) => {
    // Solo mandamos lo que cambió: el backend responde 400 si el body va vacío,
    // pero mandar el campo intacto también sería mentirle sobre lo que pasó.
    const cambios = {};
    if (valores.fullname !== cuenta.fullname) cambios.fullname = valores.fullname;
    if (valores.email !== cuenta.email) cambios.email = valores.email;

    if (Object.keys(cambios).length === 0) return;

    setGuardando(true);
    try {
      const actualizada = await actualizarMisDatos(cambios);
      message.success('Listo, tus datos quedaron guardados.');
      onActualizado?.(actualizada);
    } catch (error) {
      const detalle = error?.response?.data?.message;
      message.error(detalle || 'No pudimos guardar tus datos. Inténtalo de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={panelStyle}>
      <CardHeader
        icon={IdcardOutlined}
        tint={token.colorPrimary}
        title="Tu cuenta"
        subtitle="Los datos con los que te identificamos en la plataforma"
      />
      <div style={{ padding: token.paddingMD }}>
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{ fullname: cuenta.fullname, email: cuenta.email }}
          onFinish={guardar}
          style={{ maxWidth: 520 }}
        >
          <Form.Item label="RUT">
            <Input value={cuenta.rut} disabled />
            <Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginTop: token.marginXXS }}
            >
              Tu RUT no se puede cambiar aquí porque es lo que conecta tu nombre con los trabajos
              que quedaron registrados a tu cargo. Si está equivocado, pídenos que lo corrijamos
              desde la sección de más abajo.
            </Text>
          </Form.Item>

          <Form.Item
            name="fullname"
            label="Nombre completo"
            rules={[{ required: true, message: 'Escribe tu nombre completo.' }]}
          >
            <Input placeholder="Nombre y apellidos" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Correo electrónico"
            rules={[
              { required: true, message: 'Escribe tu correo electrónico.' },
              { type: 'email', message: 'Ese correo no parece estar bien escrito.' },
            ]}
          >
            <Input placeholder="tucorreo@ejemplo.cl" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={guardando}
                disabled={sinCambios}
              >
                Guardar cambios
              </Button>
              {!sinCambios && (
                <Button
                  onClick={() =>
                    form.setFieldsValue({ fullname: cuenta.fullname, email: cuenta.email })
                  }
                  disabled={guardando}
                >
                  Deshacer
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default CuentaPanel;
